import { ReactElement, useState } from 'react';
import {
  Button,
  Dropdown,
  Icon,
  IconSvg,
  SwitchForm,
} from '@jobcost-monorepo/ui';
import { useTranslation } from '@jobcost-monorepo/i18n';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { KeyValue, cn, useBreakpoints } from '@jobcost-monorepo/utils';
import {
  getMonthsKeyValues,
  getYearsKeyValues,
} from '@jobcost-monorepo/crud/utils/helpers/helperKeyValues';
import { TimesheetGlobalSchema } from './validationSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import FiltersTimesheetGlobal from './Filters';
import dayjs from '@jobcost-monorepo/dayjs-config';

export interface ISearch {
  month: number;
  year: number;
  details: boolean;
  isEmployee?: boolean;
  resourceId?: number;
  customerId?: number;
  jobId?: number;
}

interface IProps {
  handleSearch: (
    startDate: string,
    endDate: string,
    withDetails: boolean,
    isEmployee?: boolean,
    resourceId?: number,
    customerId?: number,
    jobId?: number,
  ) => void;
  handlePrint: () => void;
  handleSend: () => void;
}

const TimesheetReportGlobalHeader = ({
  handleSearch,
  handlePrint,
  handleSend,
}: IProps): ReactElement => {
  const { t } = useTranslation(['timesheets', 'commons']);
  const { isSmall } = useBreakpoints();
  const [filtersOpen, setFiltersOpen] = useState<boolean>(false);
  const months: KeyValue[] = getMonthsKeyValues();
  const years: KeyValue[] = getYearsKeyValues();
  const defaultValues: ISearch = {
    month: dayjs.utc().month(),
    year: dayjs.utc().year(),
    details: false,
    isEmployee: undefined,
    resourceId: undefined,
    customerId: undefined,
    jobId: undefined,
  };

  const onSubmit: SubmitHandler<ISearch> = (data) => {
    const startDate = dayjs
      .utc(`${data.year}-${data.month + 1}-01`)
      .startOf('month')
      .format('YYYY-MM-DD');
    const endDate = dayjs
      .utc(`${data.year}-${data.month + 1}-01`)
      .endOf('month')
      .format('YYYY-MM-DD');

    handleSearch(
      startDate,
      endDate,
      data.details,
      data.isEmployee,
      data.resourceId,
      data.customerId,
      data.jobId,
    );
  };

  const methods = useForm<ISearch>({
    mode: 'onSubmit',
    values: defaultValues,
    defaultValues: JSON.parse(JSON.stringify(defaultValues)),
    resolver: zodResolver(TimesheetGlobalSchema),
  });

  const { handleSubmit, getValues } = methods;

  const isFiltersActive = getValues([
    'isEmployee',
    'resourceId',
    'customerId',
    'jobId',
  ]).some((item) => item !== undefined);

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="w-full flex flex-col">
        <div
          className={cn(
            'h-max flex flex-wrap mb-4 justify-between',
            isSmall ? 'flex-col items-start' : 'flex-row items-center',
          )}
        >
          <div className="flex flew-row items-center">
            <Dropdown
              name="year"
              errorName="year"
              label={t('filter.year')}
              items={years}
              searchable
              width="w-24"
            />
            <div className="w-2" />
            <Dropdown
              name="month"
              errorName="month"
              label={t('filter.month')}
              items={months}
              searchable
              width="w-32"
            />
            <div className="w-2" />
            <div className="mt-6 pr-4">
              <SwitchForm
                id="details"
                name="details"
                label={t('filter.details')}
                width="w-fit"
              />
            </div>
            {!isSmall ? (
              <Button type="submit" className={cn(isSmall && 'w-full')}>
                <Icon iconName="search" className="fill-white stroke-white" />
                <span className="mr-2" />
                {t('filter.update')}
              </Button>
            ) : (
              <div />
            )}
          </div>
          <div
            className={cn(
              'items-center flex flex-row space-x-2',
              isSmall && 'w-full',
            )}
          >
            {isSmall ? (
              <>
                <Button type="submit" className={cn(isSmall && 'w-full')}>
                  <Icon iconName="search" className="fill-white stroke-white" />
                </Button>
              </>
            ) : (
              <div />
            )}

            <Button
              type="button"
              className={cn(isSmall && 'w-full')}
              onClick={handlePrint}
            >
              <Icon iconName="print" className="fill-white" />
              {!isSmall && (
                <div>
                  <span className="mr-2" />
                  {t('grid.print')}
                </div>
              )}
            </Button>

            <Button
              type="button"
              className={cn(isSmall && 'w-full')}
              onClick={handleSend}
            >
              <Icon iconName="mail" className="fill-white" />
              {!isSmall && (
                <div>
                  <span className="mr-2" />
                  {t('grid.send')}
                </div>
              )}
            </Button>

            <Button
              type="button"
              onClick={() => setFiltersOpen(!filtersOpen)}
              className={cn(isSmall ? 'w-full' : 'w-auto')}
            >
              <IconSvg
                name={isFiltersActive ? 'funnelIcon' : 'filterVertical'}
              />
              <div className="hidden sm:ml-1 sm:inline">
                {t('filters.title')}
              </div>
            </Button>
          </div>
        </div>

        <FiltersTimesheetGlobal
          isOpen={filtersOpen}
          handleClose={() => setFiltersOpen(false)}
          onSubmit={onSubmit}
        />
      </form>
    </FormProvider>
  );
};

export default TimesheetReportGlobalHeader;
